// Shared Header Component - Used across all pages
// Based on the Revolt prototype design

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigationManager, APP_VERSION } from '../core';
import { envUtils } from '../core/utils';
import { useAuth } from '../contexts/AuthContext';
import { RevoltButtonLink } from './RevoltButton';
import { HeaderAccountPanel, HeaderAccountPanelMobileTop, HeaderAccountPanelMobileCredits } from './HeaderAccountPanel';
import { LocalDesktopAccessRequiredModal } from './LocalDesktopAccessRequiredModal';
import { useLocation } from 'react-router-dom';

const HEADER_NAV_SCROLL_KEY = 'revolt-header-nav-scroll';

// Sections that require authentication to access (block nav if not signed in)
const SECTIONS_REQUIRING_AUTH = new Set([
  'app', 'assets', 'community', 'account', 'admin',
  'workspace', 'app-settings', 'playspace', 'desktop',
  'desktop/models', 'desktop/settings', 'desktop/dev-chat',
]);

export function Header() {
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const navigationManager = useNavigationManager();
  const { user, membershipDetails, isAuthenticated, isSupporter, isBetaTester, logout, storedAccounts, switchToAccount, removeAccount } = useAuth();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('main');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const uiMode = envUtils.device.getRecommendedUIMode();
    return uiMode === 'mobile';
  });
  const [desktopAccessDeniedModal, setDesktopAccessDeniedModal] = useState(null);

  useEffect(() => {
    const check = () => {
      const uiMode = envUtils.device.getRecommendedUIMode();
      setIsMobile(uiMode === 'mobile');
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Derive active section from URL (works on direct URL loads)
  useEffect(() => {
    const path = (location.pathname || '').toLowerCase();
    let derived = 'main';
    if (path.startsWith('/workspace')) derived = 'workspace';
    else if (path.startsWith('/playspace')) derived = 'playspace';
    else if (path.startsWith('/app-settings')) derived = 'app-settings';
    else if (path.startsWith('/app')) derived = 'app';
    else if (path.startsWith('/assets')) derived = 'assets';
    else if (path.startsWith('/downloads')) derived = 'downloads';
    else if (path.startsWith('/community')) derived = 'community';
    else if (path.startsWith('/learn')) derived = 'learn';
    else if (path.startsWith('/pricing')) derived = 'pricing';
    else if (path.startsWith('/marketplace')) derived = 'marketplace';
    else if (path.startsWith('/account')) derived = 'account';
    else if (path.startsWith('/admin')) derived = 'admin';
    else if (path.startsWith('/desktop')) derived = 'desktop';
    else if (path === '/' || path.startsWith('/main')) derived = 'main';

    setActiveSection(derived);
    navigationManager.currentSection = derived;
  }, [location.pathname, navigationManager]);

  const handleLogoClick = async () => {
    await navigationManager.goHome();
  };

  const handleNavClick = async (section) => {
    if (isMobile && navRef.current) {
      try {
        sessionStorage.setItem(HEADER_NAV_SCROLL_KEY, String(navRef.current.scrollLeft));
      } catch (_) {}
    }
    // Public sections (pricing, downloads, learn, marketplace, main, etc.) allow nav without auth
    if (SECTIONS_REQUIRING_AUTH.has(section) && !isAuthenticated) {
      const route = navigationManager.getRouteForSection(section) || `/${section}`;
      await navigationManager.requireAuth(route);
      return;
    }
    await navigationManager.navigateTo(section);
  };

  const handleUpgradeClick = async (e) => {
    e.preventDefault();
    await navigationManager.navigateTo('pricing');
  };

  const handleAccountMenuClick = async () => {
    await navigationManager.navigateTo('account');
  };

  const handleAdminClick = async () => {
    await navigationManager.navigateTo('admin');
  };

  const handleAuthClick = async () => {
    // Require authentication and return to current page after login
    await navigationManager.requireAuth(location.pathname + location.search);
  };

  const handleLogout = async () => {
    await navigationManager.goHome({
      onComplete: async () => {
        await logout();
      }
    });
  };

  const handleAccountSwitch = async (accountId) => {
    try {
      const result = await switchToAccount(accountId);
      if (!result.success) {
        if (
          result.code === 'no_local_desktop_entitlement' ||
          result.type === 'entitlement'
        ) {
          setDesktopAccessDeniedModal({
            message: result.error,
            email: result.attemptedEmail,
          });
        } else {
          console.error('Failed to switch account:', result.error);
        }
      }
      return result;
    } catch (error) {
      console.error('Account switch error:', error);
      return { success: false, error };
    }
  };

  const handleRemoveAccount = (accountId, event) => {
    event.stopPropagation();
    removeAccount(accountId);
  };

  // Helper function to check membership plan
  const getMembershipPlan = () => {
    try {
      const membership = user?.membership;
      if (membership && typeof membership === 'object' && membership.planId) {
        return membership.planId;
      }
      return '';
    } catch (error) {
      console.error('Error getting membership plan:', error);
      return '';
    }
  };

  const isFreePlan = () => getMembershipPlan() === 'free';
  const isMaxPlan = () => getMembershipPlan() === 'max';

  // Restore mobile nav scroll position after navigation (Header remounts per-route)
  useLayoutEffect(() => {
    if (!isMobile || !navRef.current) return;
    try {
      const saved = sessionStorage.getItem(HEADER_NAV_SCROLL_KEY);
      if (saved !== null) {
        const scrollLeft = parseInt(saved, 10);
        if (!isNaN(scrollLeft) && scrollLeft > 0) {
          navRef.current.scrollLeft = scrollLeft;
        }
      }
    } catch (_) {}
  }, [isMobile, location.pathname]);

  // Measure header height and set CSS variable for layout offset (avoids hardcoded 60px/120px)
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
    <LocalDesktopAccessRequiredModal
      isOpen={Boolean(desktopAccessDeniedModal)}
      message={desktopAccessDeniedModal?.message}
      email={desktopAccessDeniedModal?.email}
      onClose={() => setDesktopAccessDeniedModal(null)}
    />
    <header ref={headerRef} className="top-header">
      <a href="/main" className="header-logo-container header-logo" data-navigation-button onClick={(e) => { e.preventDefault(); handleLogoClick(); }}>
        <img
          src="https://bluedrake42.nyc3.cdn.digitaloceanspaces.com/revolt-site/site-assets/logo/revolt-logo-small-transparent.png"
          alt="Revolt"
          className="header-logo-img"
        />
        <span className="header-logo-text">RevoltGPT.dev</span>
      </a>

      {/* Mobile: row 1 right - badges + avatar */}
      {isAuthenticated && isMobile && (
        <HeaderAccountPanelMobileTop
          user={user}
          isMaxPlan={isMaxPlan()}
          isSupporter={isSupporter}
          isBetaTester={isBetaTester}
          storedAccounts={storedAccounts}
          onAccountMenuClick={handleAccountMenuClick}
          onAdminClick={handleAdminClick}
          onLogout={handleLogout}
          onAccountSwitch={handleAccountSwitch}
          onRemoveAccount={handleRemoveAccount}
        />
      )}

      <nav ref={navRef} className="header-nav">
          <a
            href="/app"
            className={`header-nav-item ${activeSection === 'app' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('app'); }}
          >
            Apps
          </a>
          <a
            href="/assets"
            className={`header-nav-item ${activeSection === 'assets' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('assets'); }}
          >
            Assets
          </a>
          <a
            href="/downloads"
            className={`header-nav-item ${activeSection === 'downloads' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('downloads'); }}
          >
            Downloads
          </a>
          <a
            href="/community"
            className={`header-nav-item ${activeSection === 'community' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('community'); }}
          >
            Community
          </a>
          <a
            href="/learn"
            className={`header-nav-item ${activeSection === 'learn' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('learn'); }}
          >
            Learn
          </a>
          <a
            href="/marketplace"
            className={`header-nav-item ${activeSection === 'marketplace' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('marketplace'); }}
          >
            Marketplace
          </a>
          <a
            href="/pricing"
            className={`header-nav-item ${activeSection === 'pricing' ? 'active' : ''}`}
            data-navigation-button
            onClick={(e) => { e.preventDefault(); handleNavClick('pricing'); }}
          >
            Pricing
          </a>
          {typeof window !== 'undefined' && window.navigationAPI && (
            <a
              href="/desktop/models"
              className={`header-nav-item ${activeSection === 'desktop' ? 'active' : ''}`}
              data-navigation-button
              onClick={(e) => { e.preventDefault(); handleNavClick('desktop/models'); }}
            >
              Desktop
            </a>
          )}
        </nav>

      <div className="version-label">
        <span aria-label={`Revolt version ${APP_VERSION}`}>{APP_VERSION}</span>
      </div>

      {/* Mobile: row 2 right - credits */}
      {isAuthenticated && isMobile && (
        <HeaderAccountPanelMobileCredits membershipDetails={membershipDetails} />
      )}

      <div className={`header-right ${isAuthenticated && isMobile ? 'header-right--mobile-hidden' : ''}`}>
        {isAuthenticated ? (
          <>
            {isFreePlan() && (
              <RevoltButtonLink href="/pricing" data-navigation-button onClick={handleUpgradeClick}>
                <i className="fa-solid fa-arrow-up"></i> Upgrade
              </RevoltButtonLink>
            )}
            <HeaderAccountPanel
              user={user}
              membershipDetails={membershipDetails}
              isMaxPlan={isMaxPlan()}
              isSupporter={isSupporter}
              isBetaTester={isBetaTester}
              storedAccounts={storedAccounts}
              onAccountMenuClick={handleAccountMenuClick}
              onAdminClick={handleAdminClick}
              onLogout={handleLogout}
              onAccountSwitch={handleAccountSwitch}
              onRemoveAccount={handleRemoveAccount}
            />
          </>
        ) : (
          // Non-authenticated user UI
          <div className="auth-buttons">
            <RevoltButtonLink href="#auth" onClick={handleAuthClick}>
              Sign In / Sign Up
            </RevoltButtonLink>
          </div>
        )}
      </div>
    </header>
    </>
  );
}
